import "./bootstrap";
import "simplebar-react/dist/simplebar.min.css";
import "flatpickr/dist/themes/light.css";
import "../scss/app.scss";
import "react-toastify/dist/ReactToastify.css";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";
import "tippy.js/themes/light-border.css";
import "tippy.js/animations/shift-away.css";
import "tippy.js/animations/scale-subtle.css";
import "tippy.js/animations/perspective-extreme.css";
import "tippy.js/animations/perspective-subtle.css";
import "tippy.js/animations/perspective.css";
import "tippy.js/animations/scale-extreme.css";
import "tippy.js/animations/scale-subtle.css";
import "tippy.js/animations/scale.css";
import "tippy.js/animations/shift-away-extreme.css";
import "tippy.js/animations/shift-away-subtle.css";
import "tippy.js/animations/shift-away.css";
import "tippy.js/animations/shift-toward-extreme.css";
import "tippy.js/animations/shift-toward-subtle.css";
import "tippy.js/animations/shift-toward.css";

import React, { useEffect, useState } from "react";
import { createInertiaApp, router } from "@inertiajs/react";
import { toast } from "react-toastify";
import { MAX_UPLOAD_SIZE_MB } from "@/constant/uploads";
import { hydrateRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { configureStore } from "@reduxjs/toolkit";
import rootReducer from "./store/rootReducer";
import AppLayout from "@/Layouts/AppLayout";
import Card from "@/Components/ui/Card";
import useHumanizedTime from "@/hooks/useHumanizeTime";
import type { Auth } from "@/types";

declare global {
    interface Window {
        __PRELOADED_STATE__?: any;
    }
}

const appName = import.meta.env.VITE_APP_NAME || "Cips Connect";

const store = configureStore({
    reducer: rootReducer,
    preloadedState: typeof window !== 'undefined' ? window.__PRELOADED_STATE__ : undefined,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware(),
});

if (typeof window !== 'undefined') {
    delete window.__PRELOADED_STATE__;

    // A 413 from the web server (client_max_body_size) is not an Inertia
    // response; without this Inertia shows the raw error page in a modal.
    router.on("invalid", (event) => {
        if (event.detail.response?.status === 413) {
            event.preventDefault();
            toast.error(
                `Upload failed: the attached file(s) are too large. Files must be ${MAX_UPLOAD_SIZE_MB}MB or smaller.`,
            );
        }
    });
}

createInertiaApp({
    title: (title: string) => `${title} - ${appName}`,
    resolve: async (name: string) => {
        const pages = import.meta.glob("./Pages/**/*.{tsx,jsx}");
        // Try .tsx first
        let page = pages[`./Pages/${name}.tsx`];
        if (!page) {
            // Fallback to .jsx
            page = pages[`./Pages/${name}.jsx`];
        }
        if (!page) {
            throw new Error(`Page not found: ./Pages/${name}`);
        }
        const module = await page() as { default: React.ComponentType };
        return module.default;
    },
    setup({ el, App, props }) {
        hydrateRoot(
            el,
            <Provider store={store}>
                <App {...props} />
            </Provider>,
        );
    },
    progress: {
        color: "#4B5563",
        showSpinner: true,
    },
});

interface DashboardProps {
    auth: Auth;
    appName: string;
}

export default function Dashboard({ auth, appName }: DashboardProps) {
    const [client, setClient] = useState(false);
    const [timeOfDay, setTimeOfDay] = useState("");
    const [avatar, setAvatar] = useState("");

    useEffect(() => {
        setClient(true);
        setTimeOfDay(useHumanizedTime());
        setAvatar(
            auth.user.profile_photo_path ||
            `https://ui-avatars.com/api?name=${auth.user.name.split(" ").join("+")}`
        );
    }, [auth.user]);

    return (
        <AppLayout title="Dashboard">
            <div className="max-w-7xl">
                <div className="grid md:grid-cols-2 grid-cols-1">
                    <Card>
                        <div className="flex space-x-4 h-full items-center rtl:space-x-reverse">
                            <div className="flex-none">
                                <div className="h-20 w-20 rounded-full overflow-hidden">
                                    {client && (
                                        <img
                                            src={avatar}
                                            alt=""
                                            className="w-full h-full"
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="flex-1">
                                <h4 className="text-xl font-medium mb-2">
                                    <span className="block font-light">
                                        {client ? timeOfDay : ""}
                                    </span>
                                    <span className="block">
                                        {auth.user.name}
                                    </span>
                                </h4>
                                <p className="text-sm dark:text-slate-300">
                                    Welcome to {appName}
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </AppLayout>
    );
}
